import { z } from 'zod'
import { createdAtSortQueryShape, creatorListQueryShape } from '../list-query/contract.ts'
import { accountDisplaySchema } from '../profile/contract.ts'
import { getContractVariable } from './variable-definitions.ts'

export {
  legacyContractVariables,
  systemContractVariables,
  getContractVariable,
  isLegacyContractVariableKey
} from './variable-definitions.ts'

export const contractTemplateTypeSchema = z.enum(['lease', 'service', 'housing_proof', 'supplement', 'other'])
export type ContractTemplateType = z.infer<typeof contractTemplateTypeSchema>

export const contractTemplateVersionStatusSchema = z.enum(['draft', 'published', 'archived'])
export const contractDocumentStatusSchema = z.enum(['confirmed', 'effective', 'voided'])

const pageSchema = z.object({
  current: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20)
})

export const contractTemplateListSchema = pageSchema.extend({
  id: z.number().int().positive().optional(),
  keyword: z.string().trim().max(255).optional(),
  type: contractTemplateTypeSchema.optional(),
  status: z.enum(['enabled', 'disabled']).optional(),
  ...creatorListQueryShape,
  ...createdAtSortQueryShape
})

const contractTemplateUpsertSchema = z.object({
  code: z
    .string()
    .trim()
    .min(2, '模板编码不能为空')
    .max(64)
    .regex(/^[a-z][a-z0-9_-]*$/, '模板编码只能使用小写字母、数字、下划线和短横线'),
  name: z.string().trim().min(1, '模板名称不能为空').max(255),
  type: contractTemplateTypeSchema,
  description: z.string().trim().max(5000).optional().nullable(),
  isEnabled: z.boolean().default(true)
})

export const contractTemplateUpsertRequestSchema = z.object({
  id: z.number().int().positive().optional(),
  data: contractTemplateUpsertSchema
})

export const contractTemplateSetEnabledSchema = z.object({
  id: z.number().int().positive(),
  isEnabled: z.boolean()
})

const contractTemplateIdsSchema = z
  .array(z.number().int().positive())
  .min(1, '至少选择一个合同模板')
  .max(100, '单次最多处理 100 个合同模板')

export const contractTemplateBatchSetEnabledSchema = z.object({
  ids: contractTemplateIdsSchema,
  isEnabled: z.boolean()
})

export const contractTemplateBatchRemoveSchema = z.object({ ids: contractTemplateIdsSchema })

export const contractTemplateBatchOperationResultSchema = z.object({ count: z.number().int().nonnegative() })

export const contractTemplateCopySchema = z.object({
  id: z.number().int().positive()
})

export const contractTemplateSchema = z.object({
  id: z.number().int().positive(),
  code: z.string(),
  name: z.string(),
  type: contractTemplateTypeSchema,
  description: z.string().nullable(),
  isEnabled: z.boolean(),
  currentVersion: z.number().int().nonnegative(),
  versionsCount: z.number().int().nonnegative(),
  latestVersion: z
    .object({
      id: z.number().int().positive(),
      version: z.number().int().positive(),
      status: contractTemplateVersionStatusSchema,
      createdAt: z.date(),
      updatedAt: z.date()
    })
    .nullable(),
  creator: accountDisplaySchema,
  createdAt: z.date(),
  updatedAt: z.date()
})

export const contractTemplateListOutputSchema = z.object({
  data: z.array(contractTemplateSchema),
  total: z.number().int().nonnegative(),
  current: z.number().int().positive(),
  pageSize: z.number().int().positive()
})

export const contractTemplateOptionSchema = z.object({
  label: z.string(),
  value: z.number().int().positive(),
  type: contractTemplateTypeSchema
})

export const contractTemplateVersionListSchema = z.object({
  templateId: z.number().int().positive()
})

const CONTRACT_CONTENT_MAX_BYTES = 512 * 1024
const CONTRACT_CONTENT_MAX_DEPTH = 50
const CONTRACT_CONTENT_MAX_NODES = 5000
const CONTRACT_TEXT_MAX_LENGTH = 20_000
// 内置变量包含 fullAddress、startDate 等驼峰字段；发布时仍由变量目录校验白名单。
const CONTRACT_VARIABLE_KEY_PATTERN = /^[a-z][a-zA-Z0-9]*(\.[a-z][a-zA-Z0-9]*)+$/

type LexicalValidationState = {
  depth: number
  nodes: number
  variableKeys: Set<string>
}

function validateLexicalNode({
  value,
  state,
  path,
  expectedType
}: {
  value: unknown
  state: LexicalValidationState
  path: string
  expectedType?: string | readonly string[]
}): string | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return `${path} 必须是对象`
  }
  if (state.nodes++ >= CONTRACT_CONTENT_MAX_NODES) {
    return '合同正文节点数量不能超过 5000 个'
  }
  if (state.depth > CONTRACT_CONTENT_MAX_DEPTH) {
    return '合同正文层级不能超过 50 层'
  }

  const node = value as Record<string, unknown>
  if (typeof node.type !== 'string') {
    return `${path}.type 不正确`
  }
  if (expectedType && (Array.isArray(expectedType) ? !expectedType.includes(node.type) : node.type !== expectedType)) {
    return `${path}.type 不符合正文结构`
  }
  if (typeof node.version !== 'number' || !Number.isInteger(node.version) || node.version < 1) {
    return `${path}.version 不正确`
  }

  if (node.type === 'text' || node.type === 'contract-variable') {
    if (typeof node.text !== 'string' || node.text.length > CONTRACT_TEXT_MAX_LENGTH) {
      return `${path}.text 不正确或过长`
    }
    if (node.type === 'contract-variable') {
      if (
        typeof node.variableKey !== 'string' ||
        (!getContractVariable(node.variableKey) && !CONTRACT_VARIABLE_KEY_PATTERN.test(node.variableKey))
      ) {
        return `${path}.variableKey 不正确`
      }
      state.variableKeys.add(node.variableKey)
    }
    return undefined
  }

  if (node.type === 'linebreak' || node.type === 'horizontalrule') {
    return undefined
  }
  if (node.type === 'list') {
    if (!['number', 'bullet', 'check'].includes(node.listType as string)) {
      return `${path}.listType 不正确`
    }
    if (typeof node.start !== 'number' || !Number.isInteger(node.start) || node.start < 1) {
      return `${path}.start 不正确`
    }
    if (node.tag !== 'ul' && node.tag !== 'ol') {
      return `${path}.tag 不正确`
    }
  }
  if (node.type === 'listitem') {
    if (typeof node.value !== 'number' || !Number.isInteger(node.value) || node.value < 1) {
      return `${path}.value 不正确`
    }
    if (node.checked !== undefined && typeof node.checked !== 'boolean') {
      return `${path}.checked 不正确`
    }
  }
  if (node.type === 'table') {
    if (
      node.colWidths !== undefined &&
      (!Array.isArray(node.colWidths) || node.colWidths.some((width) => typeof width !== 'number' || width <= 0))
    ) {
      return `${path}.colWidths 不正确`
    }
    if (node.rowStriping !== undefined && typeof node.rowStriping !== 'boolean') {
      return `${path}.rowStriping 不正确`
    }
  }
  if (node.type === 'tablerow' && node.height !== undefined) {
    if (typeof node.height !== 'number' || node.height < 0) {
      return `${path}.height 不正确`
    }
  }
  if (node.type === 'tablecell') {
    if (
      typeof node.headerState !== 'number' ||
      !Number.isInteger(node.headerState) ||
      node.headerState < 0 ||
      node.headerState > 3
    ) {
      return `${path}.headerState 不正确`
    }
    for (const field of ['colSpan', 'rowSpan'] as const) {
      if (typeof node[field] !== 'number' || !Number.isInteger(node[field]) || node[field] < 1) {
        return `${path}.${field} 不正确`
      }
    }
    if (node.width !== undefined && (typeof node.width !== 'number' || node.width <= 0)) {
      return `${path}.width 不正确`
    }
    if (
      node.backgroundColor !== undefined &&
      node.backgroundColor !== null &&
      typeof node.backgroundColor !== 'string'
    ) {
      return `${path}.backgroundColor 不正确`
    }
  }
  if (node.type === 'link' || node.type === 'autolink') {
    const url = typeof node.url === 'string' ? node.url : ''
    const isAbsoluteUrl = /^(https?:\/\/|mailto:)/i.test(url)
    const isSitePath = url.startsWith('/') && !url.startsWith('//')
    const isAnchor = url.startsWith('#')
    if (!isAbsoluteUrl && !isSitePath && !isAnchor) {
      return `${path}.url 只允许 http(s)、mailto 或站内链接`
    }
  } else if (
    ![
      'root',
      'paragraph',
      'heading',
      'quote',
      'code',
      'list',
      'listitem',
      'link',
      'autolink',
      'horizontalrule',
      'table',
      'tablerow',
      'tablecell'
    ].includes(node.type)
  ) {
    return `${path}.type 不支持`
  }
  if (node.type === 'horizontalrule') {
    return undefined
  }
  if (!Array.isArray(node.children) || node.children.length > CONTRACT_CONTENT_MAX_NODES) {
    return `${path}.children 不正确或过多`
  }
  state.depth += 1
  let childExpectedType: string | string[]
  switch (node.type) {
    case 'root':
      childExpectedType = ['paragraph', 'heading', 'quote', 'code', 'list', 'table', 'horizontalrule']
      break
    case 'list':
      childExpectedType = 'listitem'
      break
    case 'listitem':
      childExpectedType = ['text', 'contract-variable', 'linebreak', 'link', 'autolink', 'list']
      break
    case 'table':
      childExpectedType = 'tablerow'
      break
    case 'tablerow':
      childExpectedType = 'tablecell'
      break
    case 'tablecell':
      childExpectedType = ['paragraph', 'heading', 'quote', 'code', 'list', 'table', 'horizontalrule']
      break
    default:
      childExpectedType = ['text', 'contract-variable', 'linebreak', 'link', 'autolink']
      break
  }
  for (const [index, child] of node.children.entries()) {
    const error: string | undefined = validateLexicalNode({
      value: child,
      state,
      path: `${path}.children[${index}]`,
      expectedType: childExpectedType
    })
    if (error) {
      return error
    }
  }
  state.depth -= 1
  return undefined
}

function validateContractContent(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return '合同正文格式不正确'
  }
  let serialized: string
  try {
    serialized = JSON.stringify(value)
  } catch {
    return '合同正文格式不正确'
  }
  if (serialized.length > CONTRACT_CONTENT_MAX_BYTES) {
    return '合同正文不能超过 512KB'
  }
  const state: LexicalValidationState = { depth: 0, nodes: 0, variableKeys: new Set() }
  const root = (value as Record<string, unknown>).root
  return validateLexicalNode({ value: root, state, path: 'root', expectedType: 'root' })
}

export const contractContentSchema = z.record(z.string(), z.unknown()).superRefine((value, context) => {
  const error = validateContractContent(value)
  if (error) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: error })
  }
})

export function extractContractVariableKeys(value: unknown) {
  const keys = new Set<string>()
  const visit = (node: unknown) => {
    if (!node || typeof node !== 'object') {
      return
    }
    if (Array.isArray(node)) {
      let text = ''
      const collect = () => {
        for (const match of text.matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)) {
          keys.add(match[1])
        }
        text = ''
      }
      for (const child of node) {
        if (child?.type === 'text' && typeof child.text === 'string') {
          text += child.text
        } else {
          collect()
        }
        visit(child)
      }
      collect()
      return
    }
    const record = node as Record<string, unknown>
    if (record.type === 'contract-variable' && typeof record.variableKey === 'string') {
      keys.add(record.variableKey)
    }
    if (Array.isArray(record.children)) {
      visit(record.children)
    }
  }
  const root =
    value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>).root : value
  visit(root)
  return [...keys].sort()
}

export const contractTemplateSaveDraftRequestSchema = z.object({
  publish: z.boolean().optional(),
  id: z.number().int().positive().optional(),
  template: contractTemplateUpsertSchema,
  content: contractContentSchema,
  printSettings: z.record(z.string(), z.unknown()).optional().nullable(),
  changeNote: z.string().trim().max(5000).optional().nullable()
})

export const contractTemplateVersionUpsertRequestSchema = z.object({
  id: z.number().int().positive().optional(),
  templateId: z.number().int().positive(),
  data: z.object({
    content: contractContentSchema,
    printSettings: z.record(z.string(), z.unknown()).optional().nullable(),
    variableKeys: z.array(z.string().trim().min(1).max(120)).max(200).default([]),
    changeNote: z.string().trim().max(5000).optional().nullable()
  })
})

export const contractTemplateVersionSchema = z.object({
  id: z.number().int().positive(),
  templateId: z.number().int().positive(),
  version: z.number().int().positive(),
  status: contractTemplateVersionStatusSchema,
  content: z.unknown(),
  printSettings: z.unknown().nullable(),
  variableKeys: z.array(z.string()),
  changeNote: z.string().nullable(),
  creator: accountDisplaySchema,
  publishedBy: accountDisplaySchema.nullable(),
  publishedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date()
})

export const contractTemplateVersionListItemSchema = contractTemplateVersionSchema.omit({
  content: true,
  printSettings: true
})

export const contractTemplateVersionDetailSchema = z.object({
  id: z.number().int().positive()
})

export const contractTemplateVersionRestoreSchema = z.object({
  id: z.number().int().positive()
})

export const contractTemplateVersionPublishSchema = z.object({
  id: z.number().int().positive()
})

export const contractDocumentListSchema = pageSchema.extend({
  id: z.number().int().positive().optional(),
  keyword: z.string().trim().max(255).optional(),
  type: contractTemplateTypeSchema.optional(),
  status: contractDocumentStatusSchema.optional(),
  rentalOrderId: z.number().int().positive().optional(),
  serviceOrderId: z.number().int().positive().optional()
})

export const contractDocumentSchema = z.object({
  id: z.number().int().positive(),
  contractNo: z.string(),
  title: z.string(),
  type: contractTemplateTypeSchema,
  status: contractDocumentStatusSchema,
  templateId: z.number().int().positive().nullable(),
  templateName: z.string().nullable(),
  templateVersionId: z.number().int().positive().nullable(),
  templateVersion: z.number().int().positive().nullable(),
  rentalOrderId: z.number().int().positive().nullable(),
  serviceOrderId: z.number().int().positive().nullable(),
  sourceOrder: z
    .object({ type: z.enum(['rental', 'service']), id: z.number().int().positive(), orderNo: z.string() })
    .nullable(),
  content: z.unknown(),
  printSettings: z.unknown().nullable(),
  note: z.string().nullable(),
  creator: accountDisplaySchema,
  confirmedAt: z.date().nullable(),
  effectiveAt: z.date().nullable(),
  voidedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date()
})

export const contractDocumentListOutputSchema = z.object({
  data: z.array(contractDocumentSchema.omit({ content: true, printSettings: true })),
  total: z.number().int().nonnegative(),
  current: z.number().int().positive(),
  pageSize: z.number().int().positive()
})

export const contractDocumentDetailSchema = contractDocumentSchema.extend({
  sourceSnapshot: z.unknown().nullable(),
  variableSnapshot: z.unknown().nullable()
})

export const contractDocumentCreateRequestSchema = z.object({
  title: z.string().trim().min(1, '合同名称不能为空').max(255),
  templateVersionId: z.number().int().positive(),
  note: z.string().trim().max(5000).optional().nullable(),
  rentalOrderId: z.number().int().positive().optional().nullable()
})

export const contractDocumentUpdateDraftRequestSchema = z.object({
  id: z.number().int().positive(),
  data: z.object({
    title: z.string().trim().min(1, '合同名称不能为空').max(255),
    content: contractContentSchema,
    printSettings: z.record(z.string(), z.unknown()).optional().nullable(),
    note: z.string().trim().max(5000).optional().nullable()
  })
})

export const contractDocumentSetStatusRequestSchema = z.object({
  id: z.number().int().positive(),
  status: contractDocumentStatusSchema
})

export type ContractTemplateListInput = z.infer<typeof contractTemplateListSchema>
export type ContractTemplateUpsertInput = z.infer<typeof contractTemplateUpsertSchema>
export type ContractTemplateSaveDraftInput = z.infer<typeof contractTemplateSaveDraftRequestSchema>
export type ContractTemplateVersionUpsertInput = z.infer<typeof contractTemplateVersionUpsertRequestSchema>
export type ContractTemplateBatchSetEnabledInput = z.infer<typeof contractTemplateBatchSetEnabledSchema>
export type ContractTemplateBatchRemoveInput = z.infer<typeof contractTemplateBatchRemoveSchema>
export type ContractDocumentListInput = z.infer<typeof contractDocumentListSchema>

export const contractDocumentBatchTargetSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('ids'), ids: z.array(z.number().int().positive()).min(1).max(100) }),
  z.object({
    mode: z.literal('search'),
    search: contractDocumentListSchema,
    expectedCount: z.number().int().nonnegative()
  })
])
export const contractDocumentBatchSetStatusSchema = z.object({
  target: contractDocumentBatchTargetSchema,
  status: contractDocumentStatusSchema
})
export const contractDocumentBatchResultSchema = z.object({
  total: z.number().int().nonnegative(),
  updatedCount: z.number().int().nonnegative(),
  unchangedCount: z.number().int().nonnegative()
})
export type ContractDocumentStatus = z.infer<typeof contractDocumentStatusSchema>
export type ContractDocumentBatchSetStatusInput = z.infer<typeof contractDocumentBatchSetStatusSchema>
